import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  listAutoVideos,
  deleteAutoVideo,
  listSuggestions,
  generateSuggestions,
  dismissSuggestion,
  autoCreateFromSuggestion,
} from "@/lib/api";

const STATUS_STYLES = {
  running: { label: "RUNNING", classes: "text-yellow-300 border-yellow-400/40" },
  complete: { label: "READY", classes: "text-emerald-300 border-emerald-400/40" },
  failed: { label: "FAILED", classes: "text-red-300 border-red-400/40" },
};

function timeLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.max(0, (Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
}

function jobTitle(j) {
  const r = j.result || {};
  return r.script?.title || r.idea?.title || j.channel_id;
}

// Group jobs into channel buckets keyed by channel_id. Display label
// falls back gracefully if a result has no channel.name yet.
function groupByChannel(jobs) {
  const groups = new Map();
  for (const j of jobs) {
    const key = j.channel_id || "_unknown";
    if (!groups.has(key)) {
      groups.set(key, {
        channel_id: key,
        channel_name: j.result?.channel?.name || key,
        jobs: [],
      });
    }
    const g = groups.get(key);
    g.jobs.push(j);
    // Prefer a real channel name over a YouTube channel_id if we see one
    if (j.result?.channel?.name) g.channel_name = j.result.channel.name;
  }
  // Stable order: most-recently-active channel first
  return Array.from(groups.values()).sort((a, b) => {
    const at = a.jobs[0]?.created_at || "";
    const bt = b.jobs[0]?.created_at || "";
    return bt.localeCompare(at);
  });
}

export default function AutoVideos() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [generatingChannelId, setGeneratingChannelId] = useState(null);
  const [busySuggestionId, setBusySuggestionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, s] = await Promise.all([listAutoVideos(), listSuggestions()]);
      setJobs(j);
      setSuggestions(s || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load auto-videos");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async (jobId, title) => {
    const confirmed = window.confirm(
      `Delete "${title}"?\n\nThis removes the playbook + voiceover + scene images + video file. Cannot be undone.`,
    );
    if (!confirmed) return;
    setDeletingId(jobId);
    try {
      await deleteAutoVideo(jobId);
      setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
      toast.success("Auto-video deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleSuggest = useCallback(async (channelId, channelName) => {
    setGeneratingChannelId(channelId);
    try {
      const { suggestions: fresh } = await generateSuggestions(channelId, 10);
      setSuggestions((prev) => [...(fresh || []), ...prev]);
      toast.success(`Generated ${fresh?.length || 0} ideas for ${channelName}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate ideas");
    } finally {
      setGeneratingChannelId(null);
    }
  }, []);

  const handleDismiss = useCallback(async (sid) => {
    setBusySuggestionId(sid);
    try {
      await dismissSuggestion(sid);
      setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== sid));
    } catch (err) {
      toast.error("Couldn't dismiss");
    } finally {
      setBusySuggestionId(null);
    }
  }, []);

  const handleAutoCreate = useCallback(async (sid) => {
    // Immediate feedback: loading toast + busy state. The endpoint used
    // to take 5-30s because of inline Mongo lookups for the Niche
    // Playbook + channel scan; we've since deferred those into a
    // background task, but the toast also covers any future regressions.
    const toastId = toast.loading("Starting render…", {
      description: "Setting up the job — this should take a second.",
    });
    setBusySuggestionId(sid);
    try {
      const { job_id } = await autoCreateFromSuggestion(sid);
      setSuggestions((prev) => prev.filter((s) => s.suggestion_id !== sid));
      toast.success("Render queued — opening it now", { id: toastId });
      // Navigate immediately, no artificial setTimeout. The new page
      // will start polling on mount.
      window.location.href = `/auto-video/${job_id}`;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't auto-create",
        { id: toastId },
      );
      setBusySuggestionId(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => groupByChannel(jobs), [jobs]);
  const suggestionsByChannel = useMemo(() => {
    const m = new Map();
    for (const s of suggestions) {
      if (!m.has(s.channel_id)) m.set(s.channel_id, []);
      m.get(s.channel_id).push(s);
    }
    return m;
  }, [suggestions]);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1100px] mx-auto" data-testid="auto-videos-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 3 · CREATE CONTENT
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Auto-video library.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-8 leading-relaxed">
        Every video you&apos;ve shipped to the auto-video pipeline.
        To create a new one, open any channel&apos;s <b>Niche Playbook</b> from
        Top Revenue / Top Earners and click <b>▸ Auto-create this video</b>{" "}
        on any seed idea — or click <b>+ More ideas like these</b>{" "}
        on any channel below to let the AI suggest 10 fresh follow-ups.
      </p>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading<span className="blink">_</span>
        </div>
      ) : jobs.length === 0 ? (
        <div className="border border-zinc-800 rounded-sm p-10 text-center" data-testid="av-empty">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // NO AUTO-VIDEOS YET
          </div>
          <p className="text-zinc-300 text-sm max-w-md mx-auto mb-5">
            Head to <b>Top Revenue Channels</b>, click the yellow{" "}
            <span className="font-mono text-yellow-400">▸ Niche playbook</span>{" "}
            button on any row, then hit <b>▸ Auto-create this video</b> on a
            seed idea.
          </p>
          <Link
            to="/top-revenue"
            data-testid="av-empty-go"
            className="inline-block bg-yellow-400 text-black font-bold px-5 py-2.5 rounded-sm uppercase tracking-wide text-xs hover:bg-yellow-300 transition-colors"
          >
            ▸ Go to Top Revenue
          </Link>
        </div>
      ) : (
        <div className="space-y-8" data-testid="av-list">
          {grouped.map((g) => {
            const sugs = suggestionsByChannel.get(g.channel_id) || [];
            const isGenerating = generatingChannelId === g.channel_id;
            return (
              <section
                key={g.channel_id}
                data-testid={`av-channel-${g.channel_id}`}
                className="border border-zinc-800 rounded-sm overflow-hidden"
              >
                {/* Channel header */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-zinc-950/60 border-b border-zinc-900">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-0.5">
                      Channel
                    </div>
                    <div className="font-display text-base font-bold text-white truncate">
                      {g.channel_name}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                      {g.jobs.length} video{g.jobs.length === 1 ? "" : "s"}{" "}
                      shipped
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`av-more-ideas-${g.channel_id}`}
                    onClick={() => handleSuggest(g.channel_id, g.channel_name)}
                    disabled={isGenerating}
                    title="Use Claude to suggest 10 new ideas based on what you've shipped"
                    className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-3 py-2 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-50 disabled:cursor-wait"
                  >
                    {isGenerating ? "Generating…" : "+ More ideas like these"}
                  </button>
                </div>

                {/* Suggestions panel for this channel */}
                {sugs.length > 0 && (
                  <div
                    data-testid={`av-suggestions-${g.channel_id}`}
                    className="border-b border-yellow-400/20 bg-yellow-400/[0.03]"
                  >
                    <div className="px-4 pt-3 pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-300 flex items-center gap-2">
                      <span>★</span> Suggested next videos · {sugs.length}
                    </div>
                    <div className="divide-y divide-yellow-400/10">
                      {sugs.map((s) => {
                        const busy = busySuggestionId === s.suggestion_id;
                        return (
                          <div
                            key={s.suggestion_id}
                            data-testid={`av-suggestion-${s.suggestion_id}`}
                            className="px-4 py-3 flex items-start gap-3"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-white leading-snug">
                                {s.title}
                              </div>
                              {s.angle && (
                                <div className="text-xs text-zinc-400 mt-1 leading-snug">
                                  {s.angle}
                                </div>
                              )}
                              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                                {s.hook_type && (
                                  <span className="text-yellow-300">
                                    {s.hook_type.replace(/_/g, " ")}
                                  </span>
                                )}
                                {s.target_seconds ? (
                                  <span>{s.target_seconds}s target</span>
                                ) : null}
                                {s.why && (
                                  <span className="text-zinc-400 normal-case tracking-normal">
                                    · {s.why}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="shrink-0 flex flex-col gap-1.5">
                              <button
                                type="button"
                                data-testid={`av-suggestion-create-${s.suggestion_id}`}
                                onClick={() => handleAutoCreate(s.suggestion_id)}
                                disabled={busy || busySuggestionId !== null}
                                className={`font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-sm transition-colors disabled:cursor-wait inline-flex items-center gap-1.5 ${
                                  busy
                                    ? "bg-yellow-400/30 text-yellow-200 border border-yellow-400/60"
                                    : "bg-yellow-400 text-black hover:bg-yellow-300"
                                }`}
                              >
                                {busy ? (
                                  <>
                                    <span
                                      className="inline-block w-2.5 h-2.5 border-2 border-yellow-200 border-t-transparent rounded-full animate-spin"
                                      aria-hidden="true"
                                    />
                                    Queueing…
                                  </>
                                ) : (
                                  "▸ Auto-create"
                                )}
                              </button>
                              <button
                                type="button"
                                data-testid={`av-suggestion-dismiss-${s.suggestion_id}`}
                                onClick={() => handleDismiss(s.suggestion_id)}
                                disabled={busy || busySuggestionId !== null}
                                title="Remove from suggestions"
                                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 border border-zinc-800 hover:border-red-500/40 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Shipped videos for this channel */}
                <div>
                  {g.jobs.map((j) => {
                    const style = STATUS_STYLES[j.status] || STATUS_STYLES.running;
                    const r = j.result || {};
                    const title = jobTitle(j);
                    const isDeleting = deletingId === j.job_id;
                    return (
                      <div
                        key={j.job_id}
                        data-testid={`av-row-${j.job_id}`}
                        className="flex items-center gap-4 px-4 py-4 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-950/60 transition-colors"
                      >
                        <Link
                          to={`/auto-video/${j.job_id}`}
                          className="flex items-center gap-4 flex-1 min-w-0"
                        >
                          <div
                            className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] border px-2 py-1 rounded-sm ${style.classes}`}
                          >
                            {style.label}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">
                              {title}
                            </div>
                            <div className="font-mono text-[11px] text-zinc-400 truncate">
                              {r.target_seconds ? `${r.target_seconds}s target` : "—"}
                              {r.voiceover?.voice_label
                                ? ` · ${r.voiceover.voice_label}`
                                : ""}
                            </div>
                          </div>
                          <div className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                            {timeLabel(j.created_at)}
                          </div>
                        </Link>
                        <button
                          type="button"
                          data-testid={`av-delete-${j.job_id}`}
                          onClick={() => handleDelete(j.job_id, title)}
                          disabled={isDeleting}
                          title="Delete this auto-video"
                          className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 border border-zinc-800 hover:border-red-500/40 px-2.5 py-1.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? "…" : "✕ Delete"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
